import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, MessageSquare, Loader2 } from "lucide-react";
import { useNotificationCounts } from "@/hooks/useNotificationCounts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentMessagesPage() {
  const navigate = useNavigate();
  const { markSeen } = useNotificationCounts();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/parent-communication/parent/messages`);
      setMessages(res.data.messages || []);
    } catch {
      toast.error("Unable to load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { markSeen("messages"); fetchMessages(); }, []);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/parent-communication/parent/message`, { message: newMessage.trim() });
      setNewMessage("");
      toast.success("Message sent to your center");
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="parent-messages-page">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/parent")} className="rounded-full" data-testid="parent-messages-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-emerald-600" /> Messages
          </h1>
        </div>

        {/* Compose */}
        <div className="rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-200" data-testid="parent-messages-compose">
          <p className="text-sm font-semibold text-slate-700 mb-3">Send a message to your center</p>
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message here..."
            rows={3}
            className="border-slate-300"
            data-testid="parent-messages-input"
          />
          <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} className="mt-3 w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="parent-messages-send-btn">
            {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</> : <><Send className="mr-2 h-4 w-4" /> Send Message</>}
          </Button>
        </div>

        {/* Thread history */}
        {loading ? (
          <div className="flex justify-center py-8"><div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-slate-400" data-testid="parent-messages-empty">
            <MessageSquare className="mx-auto h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">No messages yet. Send your first message above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className="rounded-2xl bg-white shadow ring-1 ring-slate-200 overflow-hidden" data-testid={`parent-message-thread-${msg.id}`}>
                {/* Parent message */}
                <div className="p-4 bg-emerald-50 border-b border-emerald-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-emerald-700">You</p>
                    <p className="text-xs text-slate-400">{new Date(msg.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <p className="text-sm text-slate-800">{msg.message}</p>
                </div>
                {/* Replies */}
                {msg.replies && msg.replies.length > 0 && msg.replies.map((reply, i) => (
                  <div key={i} className="p-4 bg-white border-b border-slate-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-blue-700">{reply.from}</p>
                      <p className="text-xs text-slate-400">{new Date(reply.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    <p className="text-sm text-slate-800">{reply.message}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
